/**
 * The HR permission codes C10 adds (design §6.1).
 *
 * Named once here so a renamed code is a compile error rather than a button that
 * silently never renders. Every string is exactly what `internal/hr/permissions.go`
 * registers and what `/auth/me` returns.
 *
 * **Three splits in this list are load-bearing and none of them may be collapsed:**
 *
 *  - `assignmentManage` vs `assignmentCompensation`. A departmental coordinator may
 *    move somebody between cost centres without seeing what they earn. The assignment
 *    drawer therefore hides the salary field — and the timeline hides the column —
 *    on `manage` alone, and the server omits the field rather than zeroing it.
 *  - `exitClear` vs `exitSettle`. Whoever signs the clearance must not release the
 *    final pay; the two are an SoD pair (§6.1) and the exit screen offers each button
 *    only to its own holder, saying why when it does not.
 *  - `permitReveal`. Decrypting a permit number is its own audited act with a mandatory
 *    reason, separate from `permitManage`, exactly as `hr.employee.bank.reveal` is
 *    separate from editing a bank account.
 */

export const HR_C10_PERMISSIONS = {
  assignmentRead: 'hr.assignment.read',
  assignmentManage: 'hr.assignment.manage',
  /** See and set `basicSalary`. Deliberately not implied by `manage`. */
  assignmentCompensation: 'hr.assignment.compensation',

  payGroupRead: 'hr.pay_group.read',
  payGroupManage: 'hr.pay_group.manage',

  permitRead: 'hr.permit.read',
  permitManage: 'hr.permit.manage',
  /** Decrypt a permit number. Audited every time; reason mandatory. */
  permitReveal: 'hr.permit.reveal',
  permitQuotaManage: 'hr.permit_quota.manage',

  clockSelf: 'hr.clock.self',
  clockManage: 'hr.clock.manage',

  exceptionRead: 'hr.attendance_exception.read',
  exceptionResolve: 'hr.attendance_exception.resolve',
  attendanceLockManage: 'hr.attendance_lock.manage',

  leaveEncash: 'hr.leave.encash',
  leaveBlackoutManage: 'hr.leave_blackout.manage',
  coverageManage: 'hr.coverage.manage',

  exitRead: 'hr.exit.read',
  exitInitiate: 'hr.exit.initiate',
  /** Signs a clearance item off. Conflicts with `exitSettle`. */
  exitClear: 'hr.exit.clear',
  /** Releases the final pay. Conflicts with `exitClear`. */
  exitSettle: 'hr.exit.settle',

  scorecardRead: 'hr.scorecard.read',
  scorecardSubmit: 'hr.scorecard.submit',
  referenceManage: 'hr.reference.manage',
  rightToWorkVerify: 'hr.right_to_work.verify',

  /** ESS: one's own employment history. Held by the Employee role. */
  employeeSelfAssignments: 'hr.employee.self.assignments',
} as const

export type HrC10Permission = (typeof HR_C10_PERMISSIONS)[keyof typeof HR_C10_PERMISSIONS]
